<script lang="ts">
	// Svelte
	import { createEventDispatcher } from 'svelte';
	// DS Components
	import {
		CardGrid,
		Modal,
		SelectCard,
		Text,
		Icon,
		LinkWithIcon,
	} from '@Shelly/shelly-design-system/index';
	// Format
	import { format } from 'svelte-i18n';
	// Helpers
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
	import type { UpdateModeSubTypes } from 'src/stores/ui/edit_lists';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let showModal = false;
	export let topSectionTitle = '';
	export let bottomSectionTitle = '';
	export let itemsTop = [];
	export let itemsBottom = [];
	/**
	 * Optional third section rendered after `itemsBottom`. Used by the
	 * room kebab to append a "Change view" group without collapsing it into
	 * the bulk-actions section.
	 */
	export let extraSectionTitle = '';
	export let itemsExtra: Array<any> = [];
	export let AIModelCommands: Array<{
		name: string;
		id: UpdateModeSubTypes;
		icon: string;
	}> = [];

	const dispatch = createEventDispatcher();

	let showAiModelOptions = false;

	const onCloseModal = () => {
		showModal = !showModal;
		showAiModelOptions = false;
	};

	function handleItemClick(option: any) {
		const isAiModel =
			option.title === 'sub_settings_title_ai_model' ||
			option.titleDefault?.toLowerCase().includes('ai model');

		if (isAiModel) {
			showAiModelOptions = true;
		} else {
			if (option.onClick) {
				option.onClick();
			} else {
				dispatch('click', option);
			}

			onCloseModal();
		}
	}

	function handleAiCommandClick(modelCommand: { id: UpdateModeSubTypes }) {
		dispatch('ai-command-click', { type: 'ai_device_failure', subType: modelCommand.id });
		showAiModelOptions = false;
		onCloseModal();
	}
</script>

<Modal bind:showModal on:close={onCloseModal} hideTitle>
	<svelte:fragment slot="content">
		{#if showAiModelOptions}
			<LinkWithIcon
				class="mb-m"
				textClasses="size-m-button-min-height"
				icon="fa-light fa-chevron-left"
				text={$format('text_button_back', {
					default: 'Back',
				})}
				on:click={() => {
					showAiModelOptions = false;
				}}
			/>
			<CardGrid md={250} noPaddingBottom gridGap="s">
				{#each AIModelCommands as modelCommand}
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						hideAction
						class="width-full"
						type="horizontal"
						title={$format(modelCommand.name)}
						icon={mapIconSolidWeightToLight(modelCommand.icon)}
						on:click={() => handleAiCommandClick(modelCommand)}
					/>
				{/each}
			</CardGrid>
		{:else}
			{#if topSectionTitle}
				<Text class="mb-s" styleType="label-large">{topSectionTitle}</Text>
			{/if}

			{#if itemsTop.length}
				<CardGrid md={250} noPaddingBottom gridGap="s">
					{#each itemsTop as option}
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							hideAction
							type="horizontal"
							class="width-full"
							icon={option.icon}
							title={$format(option?.title, { default: option?.titleDefault })}
							disabled={option.disabled}
							on:click={() => handleItemClick(option)}
						/>
					{/each}
				</CardGrid>
			{/if}

			{#if itemsTop.length && itemsBottom.length}
				<hr class="mt-l mb-l" />
			{/if}

			{#if bottomSectionTitle}
				<Text class="mb-s" styleType="label-large">{bottomSectionTitle}</Text>
			{/if}

			{#if itemsBottom.length}
				<CardGrid md={250} noPaddingBottom gridGap="s">
					{#each itemsBottom as option}
						{@const isAiModel =
							option.title === 'sub_settings_title_ai_model' ||
							option.titleDefault?.toLowerCase().includes('ai model')}
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							hideAction
							type="horizontal"
							class="width-full"
							icon={mapIconSolidWeightToLight(option.icon)}
							title={$format(option?.title, { default: option?.titleDefault })}
							disabled={option.disabled}
							on:click={() => handleItemClick(option)}
							hideRightCustomAction={!isAiModel}
						>
							<Icon size="s" slot="customActionRight" icon="fa-regular fa-chevron-right" />
						</SelectCard>
					{/each}
				</CardGrid>
			{/if}

			{#if (itemsTop.length || itemsBottom.length) && itemsExtra.length}
				<hr class="mt-l mb-l" />
			{/if}

			{#if extraSectionTitle}
				<Text class="mb-s" styleType="label-large">{extraSectionTitle}</Text>
			{/if}

			{#if itemsExtra.length}
				<CardGrid md={250} noPaddingBottom gridGap="s">
					{#each itemsExtra as option}
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							hideAction
							type="horizontal"
							class="width-full"
							icon={mapIconSolidWeightToLight(option.icon)}
							title={$format(option?.title, { default: option?.titleDefault })}
							disabled={option.disabled}
							on:click={() => handleItemClick(option)}
						/>
					{/each}
				</CardGrid>
			{/if}
		{/if}
	</svelte:fragment>
</Modal>
